import { Divider, DialogContent, Button } from '@mui/material';
import AppSimpleDialog from 'app/shared-components/dialogs/AppSimpleDialog';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './GetStartedPopup.css';
import { $authHost } from 'app/configs/axiosConfig';
import { useQueryClient } from '@tanstack/react-query';
import IQueryKeys from 'src/app/types/IQueryKeys';
import { IMerchantDetailsResponse } from 'src/app/types';
import useGetStartedDialogStore from 'src/app/store/getStartedDialogSlice';
import { useCurrentMerchant } from 'src/app/queries/merchant.queries';

const steps = [
  {
    title: 'Get Started!',
    image: (
      <div className="w-full rounded-3xl my-20 overflow-hidden mb-40">
        <img
          src="/assets/images/gifs/get-started-01-desktop.gif"
          alt="Get Started Step 1"
          className="hidden md:block w-full object-contain"
        />
        <img
          src="/assets/images/gifs/get-started-01-mobile.gif"
          alt="Get Started Step 1"
          className="md:hidden w-full object-contain"
        />
      </div>
    ),
    heading: `Congratulations! You're Verified`,
    description: `Within 1-3 business days, processors will review your profile and send customized offers directly to your dashboard. You'll receive notifications via email, and you can view your offers anytime in the "Offers" section located in the left-hand navigation panel.`,
    button: 'Next',
  },
  {
    title: 'Get Started!',
    image: (
      <div className="w-full rounded-3xl my-20 overflow-hidden">
        <img
          src="/assets/images/gifs/get-started-02-desktop.gif"
          alt="Get Started Step 2"
          className="hidden md:block w-full object-contain"
        />
        <img
          src="/assets/images/gifs/get-started-02-mobile.gif"
          alt="Get Started Step 2"
          className="md:hidden w-full object-contain"
        />
      </div>
    ),
    heading: 'Speak to a Processor',
    description:
      'Need to speak directly with a processor? Click the "Speak to the Processor Now!" button located at the top of your dashboard.',
    button: 'Next',
  },
  {
    title: 'Get Started!',
    image: (
      <div className="w-full rounded-3xl my-20 overflow-hidden">
        <img
          src="/assets/images/gifs/get-started-03-desktop.gif"
          alt="Get Started Step 3"
          className="hidden md:block w-full object-contain"
        />
        <img
          src="/assets/images/gifs/get-started-03-mobile.gif"
          alt="Get Started Step 3"
          className="md:hidden w-full object-contain"
        />
      </div>
    ),
    heading: `We're Here to Help`,
    description:
      'Still have questions or need assistance? Click the "Support" button located in the left-hand navigation panel to chat with our helpful support team.',
    button: 'Go to Offers',
  },
];

const GetStartedPopup: React.FC = () => {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isOpen, closeDialog } = useGetStartedDialogStore();
  const { data: merchantData } = useCurrentMerchant();
  const isGetStartedCompleted = merchantData?.data?.isGetStartedCompleted ?? false;

  useEffect(() => {
    if (isOpen) setStep(0);
  }, [isOpen]);

  const handleNext = async () => {
    if (step < steps.length - 1) setStep(step + 1);
    else {
      if (!isGetStartedCompleted) {
        try {
          await $authHost.post('/merchant/complete-get-started');
          // Update local cache after successful request
          queryClient.setQueryData(
            [IQueryKeys.merchantInfoKey],
            (oldData: IMerchantDetailsResponse | undefined) => {
              if (oldData) {
                return {
                  ...oldData,
                  data: {
                    ...oldData.data,
                    isGetStartedCompleted: true,
                  },
                };
              }
              return oldData;
            },
          );
        } catch (error) {
          console.error('Error completing get started:', error);
        }
      }
      closeDialog(false);
      navigate('/merchant/offers');
    }
  };
  const handleBack = () => setStep((s) => (s > 0 ? s - 1 : s));

  // Step indicators
  const renderIndicators = () => (
    <div className="flex justify-center my-12">
      {steps.map((_, idx) => (
        <div
          key={idx}
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: idx === step ? '#6366f1' : '#e5e7eb',
            margin: '0 2px',
            transition: 'background 0.2s',
          }}
        />
      ))}
    </div>
  );

  return (
    <AppSimpleDialog
      open={isOpen}
      handleClose={() => closeDialog(false)}
      title="Get Started!"
      size="md"
      scroll="paper"
      maxWidth="sm"
      closeButtonClassName="merchant-popup-close-btn"
      titleClassName="merchant-title"
      hideCloseButton={!isGetStartedCompleted}
      disableEscapeKeyDown={!isGetStartedCompleted}
      onClose={(event, reason) => {
        if (isGetStartedCompleted) {
          closeDialog(false);
        }
      }}
    >
      <Divider className="my-4" sx={{ borderColor: '#e5e7eb', borderWidth: '1px' }} />
      <DialogContent className="px-12 md:px-32 pt-6 pb-40">
        {steps[step].image}
        <h3 className="text-2xl font-semibold text-gray-900 mt-10 mb-8">{steps[step].heading}</h3>
        <p className="text-lg text-gray-600 mb-20">{steps[step].description}</p>
        {renderIndicators()}
        <div className="flex justify-end mt-16 items-center">
          {step > 0 && (
            <Button
              variant="text"
              onClick={handleBack}
              className="rounded-full px-4 md:px-7 text-gray-500 font-semibold"
              style={{ minWidth: 100, marginRight: 14 }}
            >
              Back
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleNext}
            className="rounded-full px-7 bg-blue-primary hover:bg-blue-primary-hover text-white font-semibold"
            style={{ minWidth: 120 }}
          >
            {steps[step].button} <span className="ml-2">{step < 2 ? '→' : ''}</span>
          </Button>
        </div>
      </DialogContent>
    </AppSimpleDialog>
  );
};

export default GetStartedPopup;
